import { Box, Container, Divider, Paper, Typography } from "@mui/material";
import "./LegalPages.css";

const privacySections = [
  {
    number: "01",
    title: "Information We Collect",
    content: (
      <>
        <p>
          When you contact Vision X Nexus, submit an enquiry, request
          information about a property, or request a consultation, we may
          collect information that you voluntarily provide to us.
        </p>

        <ul>
          <li>Full name</li>
          <li>Phone number</li>
          <li>Email address</li>
          <li>Property requirements</li>
          <li>Preferred location</li>
          <li>Budget or investment preferences</li>
          <li>Other information you choose to provide</li>
        </ul>

        <p>
          We may also collect limited technical information such as your browser
          type, device information, IP address, pages visited, and website usage
          information where applicable.
        </p>
      </>
    ),
  },
  {
    number: "02",
    title: "How We Use Your Information",
    content: (
      <>
        <p>
          Information collected through our website may be used to provide
          better service and respond to your property requirements.
        </p>

        <ul>
          <li>Respond to your enquiries</li>
          <li>Provide property information and recommendations</li>
          <li>Arrange consultations and property viewings</li>
          <li>Contact you regarding your enquiry</li>
          <li>Assist with buying, selling, or renting property</li>
          <li>Improve our website and services</li>
          <li>Understand website performance and usage</li>
          <li>Prevent fraudulent or unauthorized activity</li>
          <li>Meet applicable legal and regulatory requirements</li>
        </ul>
      </>
    ),
  },
  {
    number: "03",
    title: "Property Information",
    content: (
      <p>
        Property prices, availability, specifications, images, features,
        locations, amenities, and other property-related information displayed
        on our website may change without prior notice. Information published on
        the website is provided for general informational purposes and should be
        independently verified before making any property or investment
        decision.
      </p>
    ),
  },
  {
    number: "04",
    title: "Sharing of Information",
    content: (
      <>
        <p>Vision X Nexus does not sell or rent your personal information.</p>

        <p>Where reasonably necessary, information may be shared with:</p>

        <ul>
          <li>Authorized employees and property consultants</li>
          <li>Property owners and developers</li>
          <li>Relevant real estate partners</li>
          <li>Website and technology service providers</li>
          <li>Professional advisers</li>
          <li>Government or regulatory authorities where required by law</li>
        </ul>
      </>
    ),
  },
  {
    number: "05",
    title: "Marketing Communications",
    content: (
      <p>
        If you provide your contact details through our website, Vision X Nexus
        or an authorized representative may contact you regarding your enquiry,
        requested services, or relevant property opportunities. You may request
        that we stop sending marketing communications at any time.
      </p>
    ),
  },
  {
    number: "06",
    title: "Analytics & Advertising",
    content: (
      <p>
        We may use website analytics and advertising technologies to understand
        website performance and measure the effectiveness of our marketing
        campaigns. Where such technologies are used, relevant information may be
        processed in accordance with applicable privacy requirements.
      </p>
    ),
  },
  {
    number: "07",
    title: "Data Security",
    content: (
      <p>
        We take reasonable technical and organizational measures to protect
        personal information against unauthorized access, misuse, alteration,
        disclosure, or loss. However, no method of electronic transmission or
        storage is completely secure, and absolute security cannot be
        guaranteed.
      </p>
    ),
  },
  {
    number: "08",
    title: "Third-Party Websites",
    content: (
      <p>
        Our website may contain links to third-party websites, property
        platforms, social media platforms, or other external services. Vision X
        Nexus is not responsible for the privacy practices or content of
        third-party websites. We recommend reviewing their respective privacy
        policies before providing personal information.
      </p>
    ),
  },
  {
    number: "09",
    title: "Children's Privacy",
    content: (
      <p>
        Our website and services are not specifically directed toward children.
        We do not knowingly collect personal information from children.
      </p>
    ),
  },
  {
    number: "10",
    title: "Your Privacy Rights",
    content: (
      <p>
        Depending on applicable law, you may have rights regarding your personal
        information, including requesting access to, correction of, or deletion
        of certain information. To make a privacy-related request, please
        contact us using the details provided below.
      </p>
    ),
  },
  {
    number: "11",
    title: "Changes to This Privacy Policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in our services, technology, or applicable legal requirements. Any
        updated version will be published on this page with a revised “Last
        Updated” date.
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <Box className="legal-page">
      {/* HERO */}
      <Box className="legal-hero legal-hero-light">
        <Container maxWidth="lg">
          <Box className="legal-hero-content">
            <Typography className="legal-overline">VISION X NEXUS</Typography>

            <Typography className="legal-main-title">Privacy Policy</Typography>

            <Typography className="legal-description">
              Your privacy is important to us. Learn how Vision X Nexus
              collects, uses, and protects your information.
            </Typography>

            <Box className="legal-meta">
              <span>Last Updated</span>
              <strong>8 October 2026</strong>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CONTENT */}
      <Container maxWidth="lg" className="legal-wrapper">
        <Paper className="legal-introduction" elevation={0}>
          <Box className="intro-number">01</Box>

          <Box>
            <Typography className="intro-title">
              Your Privacy Matters
            </Typography>

            <Typography className="intro-text">
              Vision X Nexus (“we”, “us”, “our”, or “Vision X Nexus”) respects
              your privacy and is committed to protecting the personal
              information you provide when using our website,
              <strong> visionxnexus.com</strong>.
            </Typography>

            <Typography className="intro-text">
              Vision X Nexus is a luxury real estate agency based in the UAE,
              providing property-related services including buying, selling,
              renting, property marketing, and related real estate assistance.
            </Typography>
          </Box>
        </Paper>

        <Box className="legal-sections">
          {privacySections.map((section) => (
            <Paper key={section.number} className="legal-card" elevation={0}>
              <Box className="legal-card-number">{section.number}</Box>

              <Box className="legal-card-content">
                <Typography className="legal-card-title">
                  {section.title}
                </Typography>

                <Divider className="legal-divider" />

                <Box className="legal-body">{section.content}</Box>
              </Box>
            </Paper>
          ))}

          {/* CONTACT */}
          <Paper className="legal-contact" elevation={0}>
            <Typography className="legal-contact-overline">
              HAVE QUESTIONS?
            </Typography>

            <Typography className="legal-contact-title">
              Contact Vision X Nexus
            </Typography>

            <Typography className="legal-contact-text">
              If you have questions about this Privacy Policy or how your
              information is handled, our team is available to assist you.
            </Typography>

            <Box className="legal-contact-details">
              <Box>
                <span>Phone</span>
                <strong>+971 54 791 4848</strong>
              </Box>

              <Box>
                <span>Email</span>
                <strong>info@visionxnexus.com</strong>
              </Box>

              <Box>
                <span>Address</span>
                <strong>
                  Bell Rashid Towers, Al Majaz 1, Sharjah, United Arab Emirates
                </strong>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
